import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Eye, EyeOff, Loader2, X } from 'lucide-react';
import { PASSWORD_MIN_LENGTH } from '../constants/profileConfig';
import { changePassword } from '../services/profileService';
import {
    describeChangePasswordError,
    getChangePasswordSuccessMessage,
} from '../utils/passwordErrors';
import './ChangePasswordModal.css';

const PasswordField = ({ id, label, value, onChange, visible, onToggle, autoComplete, disabled, autoFocus }) => (
    <div className="prof-modal-field">
        <label htmlFor={id}>{label}</label>
        <div className="prof-modal-input-wrap">
            <input
                id={id}
                type={visible ? 'text' : 'password'}
                value={value}
                onChange={onChange}
                autoComplete={autoComplete}
                autoFocus={autoFocus}
                disabled={disabled}
                required
            />
            <button
                type="button"
                className="prof-modal-eye"
                onClick={onToggle}
                disabled={disabled}
                aria-label={visible ? `Hide ${label}` : `Show ${label}`}
            >
                {visible ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
            </button>
        </div>
    </div>
);

/**
 * "Change password" dialog: current password, new password, confirm new password.
 * Closes with Esc, the X button, Cancel or a click outside (but not while saving).
 *
 * @param {{ onClose: () => void, onSuccess?: (message: string) => void }} props
 */
const ChangePasswordModal = ({ onClose, onSuccess }) => {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [show, setShow] = useState({ current: false, next: false, confirm: false });
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const toggle = (key) => setShow((previous) => ({ ...previous, [key]: !previous[key] }));

    // Esc closes; the page behind does not scroll while the dialog is open.
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape' && !submitting) onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = previousOverflow;
        };
    }, [onClose, submitting]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (submitting) return;

        if (newPassword.length < PASSWORD_MIN_LENGTH) {
            setError(`The new password must be at least ${PASSWORD_MIN_LENGTH} characters.`);
            return;
        }
        if (newPassword === currentPassword) {
            setError('The new password must be different from the current password.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('The new password and confirmation do not match.');
            return;
        }

        setSubmitting(true);
        setError('');
        try {
            // POST /rfp/auth/change-password  { old_password, new_password, confirm_password }
            const response = await changePassword({ currentPassword, newPassword, confirmPassword });
            if (onSuccess) onSuccess(getChangePasswordSuccessMessage(response?.data));
            onClose();
        } catch (requestError) {
            setError(describeChangePasswordError(requestError));
            setSubmitting(false);
        }
    };

    return createPortal(
        <div
            className="prof-modal-overlay"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget && !submitting) onClose();
            }}
        >
            <div className="prof-modal" role="dialog" aria-modal="true" aria-labelledby="prof-modal-title">
                <div className="prof-modal-header">
                    <h2 id="prof-modal-title">Change password</h2>
                    <button type="button" className="prof-modal-close" onClick={onClose} disabled={submitting} aria-label="Close">
                        <X size={18} aria-hidden="true" />
                    </button>
                </div>

                <form className="prof-modal-body" onSubmit={handleSubmit}>
                    {error && <div className="prof-modal-error" role="alert">{error}</div>}

                    <PasswordField
                        id="prof-current-password"
                        label="Current password"
                        value={currentPassword}
                        onChange={(event) => setCurrentPassword(event.target.value)}
                        visible={show.current}
                        onToggle={() => toggle('current')}
                        autoComplete="current-password"
                        disabled={submitting}
                        autoFocus
                    />
                    <PasswordField
                        id="prof-new-password"
                        label="New password"
                        value={newPassword}
                        onChange={(event) => setNewPassword(event.target.value)}
                        visible={show.next}
                        onToggle={() => toggle('next')}
                        autoComplete="new-password"
                        disabled={submitting}
                    />
                    <PasswordField
                        id="prof-confirm-password"
                        label="Confirm new password"
                        value={confirmPassword}
                        onChange={(event) => setConfirmPassword(event.target.value)}
                        visible={show.confirm}
                        onToggle={() => toggle('confirm')}
                        autoComplete="new-password"
                        disabled={submitting}
                    />

                    <div className="prof-modal-footer">
                        <button type="button" className="prof-btn prof-btn-ghost" onClick={onClose} disabled={submitting}>
                            Cancel
                        </button>
                        <button type="submit" className="prof-btn prof-btn-primary" disabled={submitting}>
                            {submitting && <Loader2 size={16} className="prof-spin" aria-hidden="true" />}
                            {submitting ? 'Saving...' : 'Change password'}
                        </button>
                    </div>
                </form>
            </div>
        </div>,
        document.body
    );
};

export default ChangePasswordModal;
